// The app's recent log lines, kept in memory for a feedback report: what the
// app wrote to the console (warnings and errors), errors nothing caught, and
// requests to Changeling's server that failed, with their request IDs. Lines
// are scrubbed as they are added (see scrub.ts) and only the last
// `size` are kept. Nothing leaves the browser unless the reporter ticks
// "App logs" on a report.
import type { RequestRecord } from '../api/request';
import { scrubLine, scrubPath } from './scrub';

export type Level = 'debug' | 'info' | 'warn' | 'error' | 'request';

/** One line, as a report carries it. */
export interface LogLine {
  /** When, as an ISO time. */
  at: string;
  level: Level;
  text: string;
}

/** How many lines the app keeps. */
export const LOG_SIZE = 200;

export class AppLog {
  private ring: LogLine[] = [];
  constructor(private size = LOG_SIZE) {}

  /** Adds a line at `now` (epoch ms), scrubbed. */
  add(level: Level, text: string, now = Date.now()): void {
    this.ring.push({ at: new Date(now).toISOString(), level, text: scrubLine(text) });
    if (this.ring.length > this.size) this.ring.splice(0, this.ring.length - this.size);
  }

  /** Adds what a console method was given. */
  console(level: Level, args: unknown[], now = Date.now()): void {
    this.add(level, args.map(format).join(' '), now);
  }

  /** Adds a request that failed: its method, path (scrubbed), status and ID. */
  request(r: RequestRecord): void {
    const status = r.status === null ? 'no response' : String(r.status);
    this.add('request', `${r.method} ${scrubPath(r.path)} → ${status} (request ${r.requestId})`, r.at);
  }

  /** The lines kept, oldest first. */
  lines(): LogLine[] {
    return this.ring.slice();
  }

  clear(): void {
    this.ring = [];
  }
}

function format(v: unknown): string {
  if (v instanceof Error) return `${v.name}: ${v.message}`;
  if (typeof v === 'string') return v;
  try {
    return JSON.stringify(v) ?? String(v);
  } catch {
    return String(v);
  }
}

/** The app's log. */
export const appLog = new AppLog();

let installed = false;

/**
 * Starts keeping the app's log: console warnings and errors (still written to
 * the console as before), and errors and rejections nothing caught. Once.
 */
export function installAppLog(target: Window = window): void {
  if (installed) return;
  installed = true;
  for (const level of ['warn', 'error'] as const) {
    const original = console[level].bind(console);
    console[level] = (...args: unknown[]) => {
      appLog.console(level, args);
      original(...args);
    };
  }
  target.addEventListener('error', (e) => appLog.add('error', e.error instanceof Error ? format(e.error) : String(e.message)));
  target.addEventListener('unhandledrejection', (e) => appLog.add('error', `Unhandled rejection: ${format(e.reason)}`));
}
